import { useState } from 'react'
import { Link } from 'react-router-dom'

import { useDashboard } from '@/api/queries'
import { BankIcon, CoinsIcon, PlusIcon, TicketIcon, UsersIcon } from '@/components/icons'
import { Segmented } from '@/components/ui/Controls'
import { EmptyState, ErrorState, Skeleton } from '@/components/ui/Feedback'
import { PageHeader, Section } from '@/components/ui/Page'
import { describeError } from '@/lib/errors'
import { count, money, pct } from '@/lib/format'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/store/auth'
import { DailyChart } from './DailyChart'

export function DashboardPage() {
  const [days, setDays] = useState<30 | 90>(30)
  const partner = useAuthStore((state) => state.partner)
  const query = useDashboard(days)
  const d = query.data
  const t = d?.totals

  if (query.isError && !d) {
    return (
      <>
        <PageHeader title="Dashboard" />
        <div className="card">
          <ErrorState {...describeError(query.error, 'Could not load your dashboard')} onRetry={() => void query.refetch()} />
        </div>
      </>
    )
  }

  const maxPct = d?.maxDiscountPct ?? partner?.maxDiscountPct ?? 0
  const firstName = partner?.name?.split(/\s+/)[0]

  return (
    <>
      <PageHeader
        eyebrow={partner?.tag ? `Partner ${partner.tag}` : undefined}
        title={firstName ? `Welcome back, ${firstName}` : 'Dashboard'}
        description="What your coupons have brought in so far. Amounts are in USD; commission is earned on each plan's list price."
        actions={
          <Link to="/coupons?new=1" className="inline-flex h-9 items-center gap-1.5 rounded-field bg-ink px-3.5 text-[13px] font-medium text-oncolor transition hover:bg-ink/90">
            <PlusIcon className="h-4 w-4" /> Create coupon
          </Link>
        }
      />

      {/* ---- The four numbers the partner asked for ---- */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Headline label="To be settled" value={t && money(t.pendingCents)} hint="Earned, not paid out yet" icon={<CoinsIcon />} emphasis />
        <Headline label="Overall earned" value={t && money(t.earnedCents)} hint="Pending + settled" icon={<CoinsIcon />} />
        <Headline label="Users onboarded" value={t && count(t.usersOnboarded)} hint={t ? `${count(t.paidInvoices)} paid invoices` : undefined} icon={<UsersIcon />} />
        <Headline label="Already settled" value={t && money(t.settledCents)} hint="Paid out to you" icon={<BankIcon />} />
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        {/* ---- Daily chart ---- */}
        <Section
          className="lg:col-span-2"
          title="Day by day"
          description={`Last ${days} days`}
          actions={<Segmented label="Range" value={days} onChange={setDays} options={[{ value: 30, label: '30 days' }, { value: 90, label: '90 days' }]} />}
        >
          {d ? (
            d.daily.some((p) => p.usersOnboarded > 0 || p.earnedCents > 0) ? (
              <div className={cn('transition-opacity', query.isFetching && 'opacity-60')}>
                <DailyChart daily={d.daily} />
              </div>
            ) : (
              <EmptyState title="Nothing in this period yet" description="When someone pays with one of your codes, the day shows up here with what you earned." />
            )
          ) : (
            <Skeleton className="h-[260px] w-full" />
          )}
        </Section>

        {/* ---- Revenue + max ---- */}
        <div className="grid content-start gap-4">
          <Section title="Revenue from your users" bodyClassName="p-0">
            <dl className="divide-y divide-line">
              <Row label="List price of their plans" value={t && money(t.grossCents)} />
              <Row label="Discounts given" value={t && money(t.discountCents)} />
              <Row label="Customers paid" value={t && money(t.paidCents)} strong />
              <Row label="Paid invoices" value={t && count(t.paidInvoices)} />
            </dl>
          </Section>
          <div className="card p-5">
            <p className="label-eyebrow">Your maximum discount</p>
            <p className="num mt-2 text-[30px] font-semibold leading-none text-ink">{pct(maxPct)}</p>
            <p className="mt-2 text-[13px] leading-relaxed text-ink-muted">
              Each code splits it: a <span className="font-medium text-ink-soft">10%</span> code gives the customer 10% off and you earn <span className="font-medium text-ink-soft">{pct(Math.max(0, maxPct - 10))}</span>.
            </p>
            {t && (
              <p className="mt-3 text-[12.5px] text-ink-faint">
                {count(t.couponsActive)} of {count(t.couponsCreated)} coupons active
              </p>
            )}
          </div>
        </div>
      </div>

      {/* ---- Top coupons ---- */}
      <Section
        className="mt-4"
        title="Top coupons"
        description="By what they earned you"
        actions={<Link to="/coupons" className="text-[13px] font-medium text-ink-soft hover:text-ink">All coupons</Link>}
        bodyClassName="p-0"
      >
        {!d ? (
          <div className="space-y-2 p-5">
            <Skeleton className="h-10" />
            <Skeleton className="h-10" />
          </div>
        ) : d.topCoupons.length === 0 ? (
          <EmptyState
            icon={<TicketIcon className="h-7 w-7" />}
            title="Create your first coupon and share the link"
            description="Pick a code and a discount up to your maximum. Every user who pays with it is onboarded to you, and you earn the rest of your maximum."
            action={
              <Link to="/coupons?new=1" className="inline-flex h-10 items-center justify-center gap-1.5 rounded-field bg-ink px-4 text-[14px] font-medium text-oncolor hover:bg-ink/90">
                <PlusIcon className="h-4 w-4" /> Create coupon
              </Link>
            }
          />
        ) : (
          <TopCoupons rows={d.topCoupons} />
        )}
      </Section>
    </>
  )
}

function TopCoupons({ rows }: { rows: NonNullable<ReturnType<typeof useDashboard>['data']>['topCoupons'] }) {
  const best = Math.max(1, ...rows.map((r) => r.earnedCents))
  return (
    <ul className="divide-y divide-line">
      {rows.map((row) => (
        <li key={row.id} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 gap-y-1.5 px-5 py-3 sm:grid-cols-[minmax(140px,1fr)_minmax(0,2fr)_auto]">
          <div className="min-w-0">
            <p className="num truncate text-[14px] font-semibold text-ink">{row.code}</p>
            <p className="text-[12px] text-ink-muted">
              {pct(row.discountPct)} off · you earn {pct(row.commissionPct)}
            </p>
          </div>
          <div className="order-last col-span-2 h-1.5 overflow-hidden rounded-pill bg-ink/[0.06] sm:order-none sm:col-span-1">
            <div className="h-full rounded-pill bg-brand" style={{ width: `${(row.earnedCents / best) * 100}%` }} />
          </div>
          <div className="text-right">
            <p className="num text-[14px] font-semibold text-ink">{money(row.earnedCents)}</p>
            <p className="text-[12px] text-ink-muted">{count(row.usersOnboarded)} users</p>
          </div>
        </li>
      ))}
    </ul>
  )
}

function Headline({ label, value, hint, icon, emphasis }: { label: string; value?: string; hint?: string; icon: React.ReactNode; emphasis?: boolean }) {
  return (
    <div className={cn('card min-w-0 px-4 py-3.5 sm:px-5 sm:py-4', emphasis && 'border-brand/35 bg-brand-soft/40')}>
      <p className="flex items-center justify-between gap-2 text-[12.5px] font-medium text-ink-muted sm:text-[13px]">
        <span className="truncate">{label}</span>
        <span className={cn('shrink-0 opacity-70', emphasis ? 'text-brand' : 'text-ink-faint')}>{icon}</span>
      </p>
      {value == null ? (
        <Skeleton className="mt-2 h-7 w-24" />
      ) : (
        <p className={cn('num mt-1.5 truncate text-[22px] font-semibold leading-none sm:text-[26px]', emphasis ? 'text-brand' : 'text-ink')}>{value}</p>
      )}
      {hint && <p className="mt-2 truncate text-[12px] text-ink-faint">{hint}</p>}
    </div>
  )
}

function Row({ label, value, strong }: { label: string; value?: string; strong?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3 px-5 py-3">
      <dt className="text-[13.5px] text-ink-muted">{label}</dt>
      <dd className={cn('num text-[14px]', strong ? 'font-semibold text-ink' : 'text-ink-soft')}>{value ?? '—'}</dd>
    </div>
  )
}
